import { useQuery } from '@tanstack/react-query';
import {
  projectElementsService,
  ProjectElement,
} from '@/services/project-elements';
import { apiClient } from '@/lib/api-client';

// Settings page specific hook that fetches ALL elements without org filter
export const useProjectElementsForSettings = () => {
  return useQuery({
    queryKey: ['project-elements-all'],
    queryFn: async () => {
      // Bypass organization filter to see all available elements
      const response = await apiClient.get<any[]>('/projects/elements/available');
      return response;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useProjectElements = () => {
  return useQuery({
    queryKey: ['project-elements'],
    queryFn: projectElementsService.getAvailableElements,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

export const useProjectElementsGrouped = () => {
  const { data: categories, ...rest } = useProjectElements();

  // Transform the new API response to the grouped format expected by components
  const groupedElements = categories?.reduce(
    (acc, category) => {
      if (category.isAvailable) {
        acc[category.categoryName] = category.elements;
      }
      return acc;
    },
    {} as Record<string, ProjectElement[]>
  ) || {};

  // For backward compatibility, also provide a flattened elements array
  const elements = categories?.reduce((acc, category) => {
    if (category.isAvailable) {
      acc.push(...category.elements.map(element => ({
        ...element,
        category: {
          id: category.categoryId,
          name: category.categoryName
        },
        isAvailable: category.isAvailable
      })));
    }
    return acc;
  }, [] as (ProjectElement & { category: { id: string; name: string }; isAvailable: boolean })[]) || [];

  return {
    groupedElements,
    elements,
    categories,
    ...rest,
  };
};
